import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setUser } from '@/store/slices/authSlice';
import { toast } from 'react-toastify';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginMutation, { isLoading }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await loginMutation(data).unwrap();
      // dispatch(setToken(result?.access_token));
      dispatch(setUser(result.user));
      localStorage.setItem('user', JSON.stringify(result.user));
      toast.success(result.message || 'Login successful');
      navigate(from, { replace: true });
    } catch (err) {
      // Error handled by RTK Query
      toast.error(
        (err as any)?.data?.message || 'Login failed. Please check your credentials and try again.'
      );
    }
  };

  return (
    <div className="space-y-6 relative h-screen text-black">
      <div className="w-full">
        <img src="/images/loginBg.png" alt="" className=" absolute inset-0 w-full h-fit" />
      </div>
      <div className="absolute z-10 w-full   top-[28%] left-[5%]  ">
        <h1 className="font-rokkitt font-92 text-black mb-12">LOGIN</h1>

        {/* {error && (
          <ErrorBanner
            type="error"
            title="Login Failed"
            message={(error as any)?.data?.message || 'Invalid email or password'}
          />
        )} */}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className=" pr-4 lg:pr-0 space-y-6 lg:space-y-12 lg:max-w-[550px]"
        >
          <div className="space-y-2">
            <Label htmlFor="email" className="font-rokkitt text-black font-16">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              {...register('email')}
              className={`border-0 border-b rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:border-black ${
                errors.email ? 'border-red-500' : 'border-gray-400'
              }`}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-rokkitt text-black font-16">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                {...register('password')}
                className={`border-0 border-b rounded-none bg-transparent px-0 pr-10 focus-visible:ring-0 focus-visible:border-black ${
                  errors.password ? 'border-red-500' : 'border-gray-400'
                }`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          {/* <div className="flex items-center justify-between">
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:underline"
          >
            Forgot your password?
          </Link>
        </div> */}

          <Button
            type="submit"
            className="w-fit px-12 rounded-xl border "
            variant="gradient"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Login
          </Button>
        </form>

        {/* <div className="text-center text-sm">
        <span className="text-muted-foreground">Don't have an account? </span>
        <Link to="/register" className="text-primary hover:underline">
          Sign up
        </Link>
      </div> */}

        {/* Demo credentials for testing */}
        {/* <div className="mt-6 p-4 bg-muted rounded-lg">
        <h3 className="text-sm font-medium mb-2">Demo Credentials</h3>
        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Admin:</strong> admin@example.com / admin123</p>
          <p><strong>User:</strong> user@example.com / user123</p>
        </div>
      </div> */}
      </div>
    </div>
  );
}
